import StartupPageTitle from '../../../components/StartupPageTitle';
import { ChartLineUp as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { Funnel, Export, ArrowUp, ArrowDown, Minus, Trophy, CaretDown } from '@phosphor-icons/react';

const SECTORS = [
  'B2B SaaS', 'D2C / Consumer', 'Fintech', 'Health Tech', 'EdTech',
  'Deep Tech / AI', 'Logistics', 'PropTech', 'CleanTech / Climate',
  'Agri Tech', 'Media & Entertainment', 'Gaming', 'Cyber Security',
  'HR Tech', 'Legal Tech', 'InsurTech', 'Travel & Hospitality',
  'Food & Beverage', 'Marketplace', 'Developer Tools'
];

const STAGES = ['Bootstrapped', 'Pre-seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B'];
const CITIES = ['Delhi NCR', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Kochi'];

const getPercentileColor = (pct) => {
  if (pct === null || pct === undefined) return '#4a4a5a';
  if (pct >= 75) return '#10b981';
  if (pct >= 50) return '#3b82f6';
  if (pct >= 25) return '#f59e0b';
  return '#ef4444';
};

const getPercentileLabel = (pct) => {
  if (pct === null || pct === undefined) return 'No data';
  if (pct >= 90) return 'Elite';
  if (pct >= 75) return 'Strong';
  if (pct >= 50) return 'Solid';
  if (pct >= 25) return 'Developing';
  return 'Behind';
};

const formatValue = (value, unit) => {
  if (value === null || value === undefined) return '--';
  if (unit === '₹') return `₹${value.toLocaleString('en-IN')}`;
  if (unit === '%') return `${value}%`;
  if (unit === 'x') return `${value}x`;
  if (unit === 'mo') return `${value} mo`;
  return `${value}`;
};

const MetricCard = ({ metric }) => {
  const { label, unit, your_value, rank, total, percentile } = metric;
  const color = getPercentileColor(percentile);
  const hasData = percentile !== null && percentile !== undefined;
  const topPct = hasData ? Math.max(1, 100 - percentile) : null;

  return (
    <div
      className="bg-surface border border-border rounded-[10px] overflow-hidden transition-all hover:border-[#1e1e2e] group"
      data-testid={`metric-card-${metric.key}`}
    >
      {/* Color accent bar */}
      <div className="h-[3px]" style={{ backgroundColor: color }} />

      <div className="p-5">
        {/* Percentile badge */}
        <div className="flex items-center justify-between mb-4">
          {hasData ? (
            <div className="flex items-center gap-1.5" style={{ color }}>
              {percentile >= 50
                ? <ArrowUp size={14} weight="bold" />
                : percentile >= 25
                  ? <Minus size={14} weight="bold" />
                  : <ArrowDown size={14} weight="bold" />
              }
              <span className="text-[11px] font-medium uppercase tracking-wider">{getPercentileLabel(percentile)}</span>
            </div>
          ) : (
            <span className="text-[11px] text-text-muted uppercase tracking-wider">Pending</span>
          )}
        </div>

        {/* Top % - the hero number */}
        {hasData ? (
          <div className="mb-3">
            {percentile >= 50 ? (
              <span className="text-[32px] font-mono font-bold leading-none" style={{ color }}>
                Top {topPct}%
              </span>
            ) : (
              <span className="text-[32px] font-mono font-bold leading-none" style={{ color }}>
                {percentile}<span className="text-[16px]">th %ile</span>
              </span>
            )}
          </div>
        ) : (
          <div className="mb-3">
            <span className="text-[32px] font-mono font-bold text-text-muted leading-none">--</span>
          </div>
        )}

        {/* Metric name */}
        <div className="text-[13px] text-text-secondary mb-4">{label}</div>

        {/* Divider */}
        <div className="h-px bg-border mb-3" />

        {/* Your value + rank */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] text-text-muted uppercase tracking-wider mb-0.5">Your value</div>
            <div className="text-[15px] font-mono font-semibold text-text-primary">
              {formatValue(your_value, unit)}
            </div>
          </div>
          {hasData && (
            <div className="text-right">
              <div className="text-[10px] text-text-muted uppercase tracking-wider mb-0.5">Rank</div>
              <div className="text-[15px] font-mono font-semibold text-text-primary">
                #{rank}<span className="text-text-muted">/{total}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Benchmarks = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [filters, setFilters] = useState({ stage: '', sector: '', geography: '' });
  const [appliedFilters, setAppliedFilters] = useState({ stage: '', sector: '', geography: '' });
  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fetchBenchmarks = useCallback(async (filterOverride) => {
    const f = filterOverride || appliedFilters;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.stage) params.append('stage', f.stage);
      if (f.sector) params.append('sector', f.sector);
      if (f.geography) params.append('geography', f.geography);

      const res = await api.get(`/startup/benchmarks?${params.toString()}`);
      setBenchmarkData(res.data);
    } catch (err) {
      console.error('Benchmarks fetch failed', err);
      toast.error('Failed to load benchmarks');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchBenchmarks({ stage: '', sector: '', geography: '' });
  }, []);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchBenchmarks(filters);
  };

  const handleClearFilters = () => {
    const empty = { stage: '', sector: '', geography: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    fetchBenchmarks(empty);
  };

  const handleExport = async () => {
    if (!exportRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        backgroundColor: '#0a0a0f',
        pixelRatio: 2,
        cacheBust: true,
      });
      const link = document.createElement('a');
      link.download = `founderboard-benchmarks-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Benchmark card exported!');
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Export failed — try again');
    } finally {
      setExporting(false);
    }
  };

  const metrics = benchmarkData?.metrics || [];
  const peerCount = benchmarkData?.peer_count || 0;
  const totalInFilter = benchmarkData?.total_in_filter || 0;
  const startupName = benchmarkData?.startup_name || 'Your Startup';
  const filtersApplied = benchmarkData?.filters_applied || {};

  const activeFilterCount = [filtersApplied.stage, filtersApplied.sector, filtersApplied.geography].filter(Boolean).length;

  const filterLabel = [filtersApplied.stage, filtersApplied.sector, filtersApplied.geography].filter(Boolean).join(' · ') || 'All startups';

  // Get top metrics for export card (top 3 by percentile)
  const topMetrics = [...metrics]
    .filter(m => m.percentile !== null)
    .sort((a, b) => b.percentile - a.percentile)
    .slice(0, 4);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="startup-page-header mb-6">
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl font-heading font-bold text-text-primary mb-2">Benchmarks</StartupPageTitle>
          <p className="text-text-secondary text-[13px]">
            See how you stack up. Filter your peer group. Own your numbers.
          </p>
        </div>

        {/* Filter Bar */}
        <div className="bg-surface border border-border rounded-[10px] p-4 mb-6" data-testid="filter-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[12px] text-text-muted">
              <Funnel size={14} weight="bold" />
              <span>Filter:</span>
            </div>

            <select
              value={filters.stage}
              onChange={(e) => setFilters(f => ({ ...f, stage: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-[13px] text-text-primary focus:outline-none focus:border-[#10b981]/50"
              data-testid="filter-stage"
            >
              <option value="">All Stages</option>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select
              value={filters.sector}
              onChange={(e) => setFilters(f => ({ ...f, sector: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-[13px] text-text-primary focus:outline-none focus:border-[#10b981]/50"
              data-testid="filter-sector"
            >
              <option value="">All Sectors</option>
              {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select
              value={filters.geography}
              onChange={(e) => setFilters(f => ({ ...f, geography: e.target.value }))}
              className="bg-background border border-border rounded-lg px-3 py-2 text-[13px] text-text-primary focus:outline-none focus:border-[#10b981]/50"
              data-testid="filter-geography"
            >
              <option value="">All Geographies</option>
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-[#10b981] text-white text-[13px] font-medium rounded-lg hover:bg-[#10b981]/90 transition-colors"
              data-testid="apply-filters-btn"
            >
              Apply
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="px-3 py-2 text-[12px] text-text-muted hover:text-text-primary transition-colors"
                data-testid="clear-filters-btn"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Peer Count Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="text-[13px] text-text-secondary">
              Comparing against
              <span className="font-mono font-semibold text-text-primary mx-1">{peerCount}</span>
              {peerCount === 1 ? 'startup' : 'startups'}
              {peerCount > 0 && peerCount < totalInFilter && (
                <span className="text-text-muted"> ({totalInFilter} matched, {peerCount} with data)</span>
              )}
            </div>
            {activeFilterCount > 0 && (
              <div className="px-2 py-0.5 bg-[#10b981]/10 border border-[#10b981]/20 rounded text-[11px] text-[#10b981] font-medium">
                {filterLabel}
              </div>
            )}
          </div>

          {peerCount > 0 && peerCount <= 5 && (
            <div className="text-[11px] text-warning">
              Small peer group — results may vary as more startups join
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-text-secondary">Crunching the numbers...</div>
          </div>
        ) : peerCount === 0 ? (
          /* Empty State */
          <div className="bg-surface border border-border rounded-[10px] p-12 text-center mb-6">
            <Trophy size={40} className="text-text-muted mx-auto mb-4" weight="light" />
            <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">No peers found</h3>
            <p className="text-text-secondary text-[13px] max-w-md mx-auto">
              {activeFilterCount > 0
                ? 'Try widening your filters. There aren\'t enough startups in this specific combination yet.'
                : 'Submit your first monthly update to appear in benchmarks.'}
            </p>
          </div>
        ) : (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8" data-testid="metrics-grid">
              {metrics.map((m) => (
                <MetricCard key={m.key} metric={m} />
              ))}
            </div>

            {/* Export Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-text-primary">Benchmark Card</h3>
                <button
                  onClick={handleExport}
                  disabled={exporting || topMetrics.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-surface border border-border rounded-lg text-[13px] text-text-secondary hover:text-text-primary hover:border-[#10b981]/50 transition-all disabled:opacity-50"
                  data-testid="export-btn"
                >
                  <Export size={14} weight="bold" />
                  {exporting ? 'Exporting...' : 'Download as Image'}
                </button>
              </div>

              {/* Export Card — what gets captured */}
              <div
                ref={exportRef}
                className="bg-[#0a0a0f] border border-border rounded-[12px] p-8 max-w-2xl"
                data-testid="export-card"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-[18px] font-heading font-bold text-text-primary">{startupName}</div>
                    <div className="text-[12px] text-text-muted mt-0.5">
                      {filterLabel} {peerCount > 0 && `· ${peerCount} startups compared`}
                    </div>
                  </div>
                  <div className="text-[11px] text-text-muted font-mono">FOUNDERBOARD</div>
                </div>

                {/* Metric Strips */}
                {topMetrics.length > 0 ? (
                  <div className="space-y-4">
                    {topMetrics.map((m) => {
                      const color = getPercentileColor(m.percentile);
                      const topPct = Math.max(1, 100 - m.percentile);
                      return (
                        <div key={m.key} className="flex items-center gap-4">
                          <div className="w-[70px] text-right">
                            <span className="text-[20px] font-mono font-bold" style={{ color }}>
                              {m.percentile >= 50 ? `Top ${topPct}%` : `${m.percentile}th`}
                            </span>
                          </div>
                          <div className="flex-1 h-[4px] bg-border rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${m.percentile}%`, backgroundColor: color }}
                            />
                          </div>
                          <div className="w-[140px]">
                            <div className="text-[13px] text-text-primary">{m.label}</div>
                            <div className="text-[11px] font-mono text-text-muted">
                              {formatValue(m.your_value, m.unit)} · #{m.rank}/{m.total}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-text-muted text-sm">
                    Submit a monthly update to generate your benchmark card.
                  </div>
                )}

                {/* Card Footer */}
                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                  <div className="text-[10px] text-text-muted">
                    Data from Founderboard · Anonymous peer comparison · {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-surface border border-border rounded-[10px] p-5">
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-3">How to read</div>
              <div className="flex flex-wrap gap-6">
                {[
                  { label: 'Top 25% — Strong', color: '#10b981' },
                  { label: 'Top 50% — Solid', color: '#3b82f6' },
                  { label: 'Top 75% — Developing', color: '#f59e0b' },
                  { label: 'Bottom 25% — Behind', color: '#ef4444' }
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 text-[12px] text-text-secondary">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }} />
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Benchmarks;
